<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Calendar for picking one date, or several individual ones.
 *
 * With `multiple` set, `modelValue` becomes an array of dates rather than a single one.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Selecting a single date from a visual calendar
 * @useWhen Selecting several individual dates, which a range picker cannot express
 * @related HrDateRange
 * @related HrTimeField
 * @a11y Calendar days are keyboard-focusable: Arrow keys move between days, Enter/Space selects. The surrounding
 *   context (e.g. dialog title or a labeled field) should name the picker, as the calendar has no intrinsic
 *   accessible label.
 * @see https://quasar.dev/vue-components/date Quasar q-date API
 */
defineOptions({ name: 'HrDate', inheritAttrs: false });

const props = defineProps({
  /**
   * Selected date as 'YYYY/MM/DD', or an array of them when `multiple` is set.
   */
  modelValue: {
    type: [String, Array],
    default: null,
  },
  /**
   * Predicate controlling which days are selectable; receives a 'YYYY/MM/DD' string and returns a boolean.
   */
  options: {
    type: Function,
    default: null,
  },
  /**
   * Month/year to focus initially when modelValue is empty, as 'YYYY/MM'. Ignored once a value is set.
   * @example 2026/04
   */
  defaultYearMonth: {
    type: String,
    default: null,
  },
  /**
   * Allows several individual dates to be picked, making modelValue an array. Declared rather than
   * left to attrs because it changes the shape of modelValue, not the calendar's appearance.
   */
  multiple: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the calendar. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props for backwards compatibility with the legacy theme.
   * @deprecated Only for backwards compatibility — remove once legacy consumers are migrated.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the selected date changes. */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = ['hr-date'];

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    ...(props.options !== null && { options: props.options }),
    ...(props.defaultYearMonth !== null && { defaultYearMonth: props.defaultYearMonth }),
    multiple: props.multiple,
    disable: props.disable,
  };

  if (hasTheme.value) {
    return { ...base, flat: true, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-date
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    @update:model-value="emit('update:modelValue', $event)"
  />
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-date {
  box-shadow: none;
  border-radius: var(--app-shape-corner-medium);
  background: var(--app-surface-container-high);
  color: var(--app-on-surface);
}

/*
 * Quasar hardcodes #fff text on this header (assumes a dark primary); the M3 dark theme's
 * primary is light, so the on-primary token (dark text) is used instead for correct contrast.
 */
.hr-date :deep(.q-date__header) {
  background-color: var(--app-primary);
  color: var(--app-on-primary);
}

/*
 * The selected day renders via QBtn's color/text-color props, which resolve to
 * Quasar's own !important utility classes (.bg-primary/.text-white) — matched here.
 */
.hr-date :deep(.q-date__calendar-item .bg-primary) {
  background: var(--app-primary) !important;
}

.hr-date :deep(.q-date__calendar-item .text-white) {
  color: var(--app-on-primary) !important;
}

.hr-date :deep(.q-date__today) {
  box-shadow: 0 0 0 1px var(--app-primary);
}

</style>
